/*
MIT LICENSE
Copyright (c) 2020-present Alibaba Cloud Design
Copied from antd/es/date-picker/generatePicker/generateSinglePicker.tsx
Copyright (c) 2015-present Ant UED, https://xtech.antfin.com/

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE. */
import React from 'react';
import classNames from 'classnames';
import RCPicker from 'rc-picker';
import zhCN from 'antd/es/date-picker/locale/zh_CN';
import { getPlaceholder } from 'antd/es/date-picker/util';
import warning from 'antd/es/_util/warning';
import { ConfigContext } from '../../config-provider';
import { withConfigProvider } from '../../config-provider/util';
import LocaleReceiver from 'antd/es/locale-provider/LocaleReceiver';
import SizeContext from '../../config-provider/SizeContext';
import { getTimeProps, Components } from '.';
import { getDropdownClassName, getIcons } from './_util';
export default function generatePicker(generateConfig) {
    function getPicker(picker, displayName) {
        class Picker extends React.Component {
            static contextType = ConfigContext;
            static displayName;
            pickerRef = React.createRef();
            constructor(props) {
                super(props);
                if (process.env.NODE_ENV !== 'production')
                    warning(picker !== 'quarter', displayName, `DatePicker.${displayName} is legacy usage. Please use DatePicker[picker='${picker}'] directly.`);
            }
            focus = () => {
                if (this.pickerRef.current) {
                    this.pickerRef.current.focus();
                }
            };
            blur = () => {
                if (this.pickerRef.current) {
                    this.pickerRef.current.blur();
                }
            };
            renderPicker = (contextLocale) => {
                const locale = { ...contextLocale, ...this.props.locale };
                const { getPrefixCls, direction, getPopupContainer, animation } = this.context;
                const { prefixCls: customizePrefixCls, getPopupContainer: customizeGetPopupContainer, className, size: customizeSize, bordered = true, placeholder, dropdownClassName, ...restProps } = this.props;
                warning(!('theme' in this.props), 'date-picker', `'themes' is deprecated now`);
                warning(bordered !== false, 'date-picker', `'bordered = false' is deprecated now`);
                const { format, showTime } = this.props;
                const prefixCls = getPrefixCls('picker', customizePrefixCls);
                const additionalProps = {
                    showToday: false
                };
                let additionalOverrideProps = {};
                if (picker) {
                    additionalOverrideProps.picker = picker;
                }
                const mergedPicker = picker || this.props.picker;
                additionalOverrideProps = {
                    ...additionalOverrideProps,
                    ...(showTime ? getTimeProps({ format, picker: mergedPicker, ...showTime }) : {}),
                    ...(mergedPicker === 'time' ? getTimeProps({ format, ...this.props, picker: mergedPicker }) : {})
                };
                const rootPrefixCls = getPrefixCls();
                return (React.createElement(SizeContext.Consumer, null, (size) => {
                    const mergedSize = customizeSize || size;
                    return (React.createElement(RCPicker, { ref: this.pickerRef, placeholder: getPlaceholder(mergedPicker, locale, placeholder), ...getIcons(mergedPicker), allowClear: true, dropdownClassName: getDropdownClassName(dropdownClassName, prefixCls, animation), transitionName: `${rootPrefixCls}-slide-up`, ...additionalProps, ...restProps, ...additionalOverrideProps, locale: locale.lang, className: classNames({
                            [`${prefixCls}-${mergedSize}`]: mergedSize,
                            [`${prefixCls}-bdesign`]: true
                        }, className), prefixCls: prefixCls, getPopupContainer: customizeGetPopupContainer || getPopupContainer, generateConfig: generateConfig, components: Components, direction: direction, bordered: true }));
                }));
            };
            render() {
                return (React.createElement(LocaleReceiver, { componentName: 'DatePicker', defaultLocale: zhCN }, this.renderPicker));
            }
        }
        if (displayName) {
            Picker.displayName = displayName;
        }
        return withConfigProvider(Picker);
    }
    const DatePicker = getPicker();
    const WeekPicker = getPicker('week', 'WeekPicker');
    const MonthPicker = getPicker('month', 'MonthPicker');
    const YearPicker = getPicker('year', 'YearPicker');
    const TimePicker = getPicker('time', 'TimePicker');
    const QuarterPicker = getPicker('quarter', 'QuarterPicker');
    return { DatePicker, WeekPicker, MonthPicker, YearPicker, TimePicker, QuarterPicker };
}
